import { useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import { supabase } from "@/integrations/supabase/client";
import { isNative } from "@/lib/platform";

declare global {
  interface Window {
    OneSignalDeferred?: Array<(os: any) => void | Promise<void>>;
  }
}

/**
 * Best-effort OneSignal web push wiring.
 * - Web only (native push is handled by Capacitor separately).
 * - Links the Clerk user id as the OneSignal external id.
 * - Persists the OneSignal subscription id into push_tokens via save_push_subscription.
 * - Only saves a subscription that has actually opted in (has a real token).
 * Never throws; silently no-ops when the SDK isn't available.
 */
export function useOneSignal() {
  const { isSignedIn, user } = useUser();

  useEffect(() => {
    if (isNative) return;
    if (!isSignedIn || !user?.id) return;
    if (typeof window === "undefined") return;

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    const persist = async (subId?: string | null) => {
      if (cancelled || !subId) return;
      try {
        await supabase.rpc("save_push_subscription" as any, {
          _clerk_user_id: user.id,
          _onesignal_player_id: subId,
          _platform: "web",
        });
      } catch {
        /* best-effort */
      }
    };

    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal: any) => {
      try {
        if (cancelled) return;

        try {
          await OneSignal.login(user.id);
        } catch {
          /* login can fail if already linked */
        }

        const sub = OneSignal?.User?.PushSubscription;

        // Only save a subscription that has actually opted in (has a real token).
        if (sub?.optedIn === true && sub?.id) {
          await persist(sub.id);
        }

        // ...and whenever the subscription changes (e.g. after they accept the prompt).
        const onChange = (event: any) => {
          const cur = event?.current;
          if (cur?.optedIn === true && cur?.id) void persist(cur.id);
        };

        sub?.addEventListener?.("change", onChange);
        cleanup = () => sub?.removeEventListener?.("change", onChange);
      } catch {
        /* best-effort */
      }
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [isSignedIn, user?.id]);
}
